/**
 * Mini App Entry Point
 * Hotel Booking LIFF Application
 */

import './styles.css';
import 'litepicker/dist/css/litepicker.css';

import { initLiff, isLoggedIn, login, getUserProfile, isInLineApp } from './liff';
import { setLanguage, detectLanguage, t, getCurrentLanguage } from './i18n';
import { initTenantFromUrl } from './config';
import {
  getState,
  setState,
  subscribe,
  setUser,
  setLoading,
  setError,
  navigateTo,
  setGuestInfo,
} from './store';
import { LoadingSpinner, ErrorMessage } from './components';
import {
  renderSearchPage,
  initSearchPage,
  renderRoomsPage,
  initRoomsPage,
  renderGuestInfoPage,
  initGuestInfoPage,
  loadUserProfileAsync,
  renderConfirmPage,
  initConfirmPage,
  renderPaymentPage,
  initPaymentPage,
  renderSuccessPage,
  initSuccessPage,
  renderMyBookingsPage,
  initMyBookingsPage,
  renderRoomTypesPage,
  initRoomTypesPage,
  renderSpaPage,
  initSpaPage,
  renderRestaurantPage,
  initRestaurantPage,
} from './pages';

// App container
const app = document.getElementById('app')!;

/**
 * Render the current page based on state
 */
function render(): void {
  const state = getState();

  // Show loading
  if (state.loading) {
    app.innerHTML = LoadingSpinner();
    return;
  }

  // Show error with retry
  if (state.error) {
    app.innerHTML = ErrorMessage(state.error, () => {
      setError(null);
      render();
    });

    // Set up retry handler
    window.handleRetry = () => {
      setError(null);
      render();
    };
    return;
  }

  // Render current page
  switch (state.currentPage) {
    case 'search':
      app.innerHTML = renderSearchPage();
      initSearchPage();
      break;

    case 'rooms':
      app.innerHTML = renderRoomsPage();
      initRoomsPage();
      break;

    case 'guest-info':
      // Load user profile from API before rendering (async)
      loadUserProfileAsync().finally(() => {
        app.innerHTML = renderGuestInfoPage();
        initGuestInfoPage();
      });
      // Show loading while fetching profile
      app.innerHTML = LoadingSpinner();
      return; // Don't continue, will re-render after profile is loaded

    case 'confirm':
      app.innerHTML = renderConfirmPage();
      initConfirmPage();
      break;

    case 'payment':
      app.innerHTML = renderPaymentPage();
      initPaymentPage();
      break;

    case 'success':
      app.innerHTML = renderSuccessPage();
      initSuccessPage();
      break;

    case 'my-bookings':
      app.innerHTML = renderMyBookingsPage();
      initMyBookingsPage();
      break;

    case 'room-types':
      app.innerHTML = renderRoomTypesPage();
      initRoomTypesPage();
      break;

    case 'spa':
      app.innerHTML = renderSpaPage();
      initSpaPage();
      break;

    case 'restaurant':
      app.innerHTML = renderRestaurantPage();
      initRestaurantPage();
      break;

    default:
      app.innerHTML = renderSearchPage();
      initSearchPage();
  }
  (window as any).renderApp = render;
}

/**
 * Initialize the application
 */
async function initApp(): Promise<void> {
  setLoading(true);

  try {
    // Read query param and stored language override (if any)
    const urlParams = new URLSearchParams(window.location.search);

    // Resolve tenant ID from URL ?tenant= param (or fallback to env)
    const resolvedTenant = initTenantFromUrl();
    console.log('[MiniApp] Tenant resolved:', resolvedTenant);

    if (!resolvedTenant) {
      setLoading(false);
      const root = document.getElementById('app');
      if (root) {
        root.innerHTML = `
          <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:24px;text-align:center;background:#f9f9f9;">
            <div style="font-size:64px;margin-bottom:16px;">⚠️</div>
            <h2 style="color:#cf1322;margin-bottom:8px;font-size:20px;">
              ไม่พบข้อมูลโรงแรม
            </h2>
            <p style="color:#666;margin-bottom:24px;font-size:14px;">กรุณาเข้าใช้งานผ่านลิงก์ของโรงแรมโดยตรง</p>
          </div>`;
      }
      return;
    }

    // Early intercept: if Stripe redirected here in external browser with
    // redirect_status, show a "close this page" screen immediately —
    // before LIFF init / login which would redirect away.
    const redirectStatus = urlParams.get('redirect_status');
    if (redirectStatus) {
      setLoading(false);
      const root = document.getElementById('app');
      if (root) {
        const isSuccess = redirectStatus === 'succeeded';
        root.innerHTML = `
          <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:24px;text-align:center;background:#f9f9f9;">
            <div style="font-size:64px;margin-bottom:16px;">${isSuccess ? '✅' : '❌'}</div>
            <h2 style="color:${isSuccess ? '#00B900' : '#cf1322'};margin-bottom:8px;font-size:20px;">
              ${isSuccess ? 'ชำระเงินสำเร็จ' : 'การชำระเงินไม่สำเร็จ'}
            </h2>
            <p style="color:#666;margin-bottom:24px;font-size:14px;">กรุณาปิดหน้านี้และกลับไปที่แอป LINE</p>
            <button onclick="window.close()" style="padding:12px 32px;background:${isSuccess ? '#00B900' : '#cf1322'};color:white;border:none;border-radius:8px;font-size:16px;cursor:pointer;">ปิดหน้านี้</button>
          </div>`;
      }
      return;
    }

    const paramLang = urlParams.get('lang');
    const validLangs = ['th', 'en', 'zh'];
    const storedLang = typeof localStorage !== 'undefined' ? localStorage.getItem('languageOverride') : null;
    let languageOverridden = false;

    if (paramLang && validLangs.includes(paramLang)) {
      setLanguage(paramLang as any);
      try { localStorage.setItem('languageOverride', paramLang); } catch (e) { /* ignore */ }
      // Expose stored value for E2E (helps when storage APIs are restricted in tests)
      // @ts-ignore
      (window as any).__APP_LANG_STORED__ = paramLang;
      languageOverridden = true;
    }

    // Initialize LIFF (will auto-detect demo mode)
    await initLiff();

    // Check login status (support E2E mode to avoid redirect)
    const skipLogin = urlParams.get('e2e') === '1' || urlParams.get('e2e') === 'true';
    if (!skipLogin) {
      if (!isLoggedIn()) {
        login();
        return;
      }
    }

    // Get user profile (if fetching fails in E2E mode, fall back to a demo profile)
    let profile;
    try {
      profile = await getUserProfile();
    } catch (err) {
      profile = {
        userId: 'e2e-user',
        displayName: 'E2E User',
        pictureUrl: undefined,
        language: navigator.language || 'th',
      } as any;
    }

    // If language not overridden by query param, use stored override or LIFF/browser detection
    // Expose detected profile language for debugging / E2E
    // @ts-ignore
    (window as any).__APP_DETECTED_LANG__ = profile.language;

    if (!languageOverridden) {
      if (storedLang && validLangs.includes(storedLang)) {
        setLanguage(storedLang as any);
        // @ts-ignore
        (window as any).__APP_LANG_SOURCE__ = 'stored';
      } else {
        const detected = detectLanguage(profile.language);
        setLanguage(detected);
        // @ts-ignore
        (window as any).__APP_LANG_SOURCE__ = 'detected';
        // @ts-ignore
        (window as any).__APP_DETECTED = detected;
      }
    } else {
      // @ts-ignore
      (window as any).__APP_LANG_SOURCE__ = 'param';
    }

    // Set user in state
    setUser(profile);

    // Fetch Active Services Config (Rooms, Spa, Restaurant)
    const { fetchActiveServicesConfig } = await import('./api');
    const activeServices = await fetchActiveServicesConfig();

    // Check for page parameter in URL
    const page = urlParams.get('page');
    const pendingPayment = localStorage.getItem('stripe_return_booking');

    if (pendingPayment) {
      navigateTo('payment');
    } else if (page === 'my-bookings') {
      navigateTo('my-bookings');
    } else if (page === 'room-types') {
      navigateTo('room-types');
    } else if (page === 'spa' && activeServices.spa) {
      navigateTo('spa');
    } else if (page === 'restaurant' && activeServices.restaurant) {
      navigateTo('restaurant');
    } else {
      // Auto-route to first active service tab
      if (activeServices.rooms) {
        navigateTo('search');
      } else if (activeServices.spa) {
        navigateTo('spa');
      } else if (activeServices.restaurant) {
        navigateTo('restaurant');
      } else {
        navigateTo('search');
      }
    }

    // Expose current language getter for E2E tests
    // @ts-ignore
    (window as any).__APP_GET_LANGUAGE__ = getCurrentLanguage;
    // @ts-ignore
    (window as any).__APP_CURRENT_LANG__ = getCurrentLanguage();

    console.log('App initialized:', {
      user: profile.displayName,
      language: getCurrentLanguage(),
      inLineApp: isInLineApp(),
      page: page || 'booking (default)',
    });
  } catch (error) {
    console.error('Failed to initialize app:', error);
    setError(t('errorGeneric'));
  } finally {
    setLoading(false);
  }
}

// Subscribe to state changes and re-render
subscribe(() => {
  render();
});

// Helper: Save current form data before re-render
function saveGuestFormData(): void {
  const form = document.getElementById('guest-form') as HTMLFormElement;
  if (form) {
    const formData = new FormData(form);
    setGuestInfo({
      firstName: (formData.get('firstName') as string)?.trim() || '',
      lastName: (formData.get('lastName') as string)?.trim() || '',
      email: (formData.get('email') as string)?.trim() || '',
      phone: (formData.get('phone') as string)?.trim() || '',
      specialRequests: (formData.get('specialRequests') as string)?.trim() || undefined,
    });
  }
}

// Global language switcher handlers
window.switchLanguage = async (lang: 'th' | 'en' | 'zh') => {
  // Save form data before switching language (if on guest-info page)
  const state = getState();
  if (state.currentPage === 'guest-info') {
    saveGuestFormData();
  }

  setLanguage(lang);
  try { localStorage.setItem('languageOverride', lang); } catch (e) { /* ignore */ }
  // Expose stored value for E2E
  // @ts-ignore
  (window as any).__APP_LANG_STORED__ = lang;
  // Close dropdown
  document.querySelector('.lang-dropdown')?.classList.remove('open');

  // If on rooms or confirm page, we should re-fetch room search results to get descriptions in the new language
  if ((state.currentPage === 'rooms' || state.currentPage === 'confirm') && state.search) {
    const { searchRooms } = await import('./api');
    
    // Set loading state in the main store so spinner shows up
    setState({ loading: true });
    
    try {
      const freshRooms = await searchRooms(state.search);
      
      // Update store with fresh rooms (which now have new language descriptions)
      // If a room was already selected, update it too
      let updatedSelectedRoom = null;
      if (state.selectedRoom) {
        updatedSelectedRoom = freshRooms.find(
          r => r.roomTypeCode === state.selectedRoom!.roomTypeCode && 
               r.ratePlanCode === state.selectedRoom!.ratePlanCode
        ) || null;
      }
      
      setState({
        availableRooms: freshRooms,
        selectedRoom: updatedSelectedRoom,
        loading: false,
      });
    } catch (err) {
      console.error('[LanguageSwitch] Failed to refresh availability on language change:', err);
      setState({ loading: false });
    }
  } else if (state.currentPage === 'room-types') {
    // If on room-types page, re-run initialization to fetch data in new language
    const { initRoomTypesPage } = await import('./pages/room-types');
    // initRoomTypesPage handles loading state and rendering automatically
    initRoomTypesPage();
    return;
  }

  render(); // Re-render with new language
};

window.toggleLangDropdown = () => {
  const dropdown = document.querySelector('.lang-dropdown');
  dropdown?.classList.toggle('open');
};

// Global navigation handlers
(window as any).navigateTo = (page: any) => {
  navigateTo(page);
};
window.goHome = () => {
  navigateTo('search');
};

// Close dropdown when clicking outside
document.addEventListener('click', e => {
  const dropdown = document.querySelector('.lang-dropdown');
  if (dropdown && !dropdown.contains(e.target as Node)) {
    dropdown.classList.remove('open');
  }
});

// Start the app
initApp();

// Extend window for global handlers
declare global {
  interface Window {
    handleRetry?: () => void;
    switchLanguage?: (lang: 'th' | 'en' | 'zh') => void;
    toggleLangDropdown?: () => void;
    goHome?: () => void;
  }
}
